import React, { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './About.css';

const About = () => {
  const yearsExperience = new Date().getFullYear() - 2018;
  const sectionRef = useRef(null);

  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const section = sectionRef.current;
    if (!section) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const liteEffects =
      reduceMotion ||
      window.matchMedia('(max-width: 900px)').matches ||
      window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    const lines = section.querySelectorAll('.aboutReveal');
    const emission = section.querySelector('.aboutEmission');

    if (reduceMotion) {
      gsap.set(lines, { clearProps: 'all', opacity: 1 });
      if (emission) gsap.set(emission, { opacity: 0 });
      return undefined;
    }

    const ctx = gsap.context(() => {
      // Mobile: opacity/transform only — blur filters are expensive on weak GPUs.
      gsap.set(lines, {
        opacity: 0,
        x: liteEffects ? 36 : 110,
        y: liteEffects ? 12 : 28,
        scale: liteEffects ? 0.96 : 0.86,
        rotate: liteEffects ? 0 : -1.2,
        skewX: liteEffects ? 0 : 10,
        ...(liteEffects ? {} : { filter: 'blur(16px)' }),
        transformOrigin: '80% 50%'
      });
      if (emission && !liteEffects) {
        gsap.set(emission, {
          opacity: 0,
          scale: 0.35,
          x: 40
        });
      } else if (emission) {
        gsap.set(emission, { opacity: 0 });
      }

      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section,
          start: 'top 78%',
          end: 'top 22%',
          scrub: liteEffects ? 0.6 : 1.35
        }
      });

      // Soft photon / accretion wash blooms first, then text ejects and settles.
      if (emission && !liteEffects) {
        tl.to(
          emission,
          {
            opacity: 0.85,
            scale: 1.15,
            x: 0,
            duration: 0.45
          },
          0
        ).to(
          emission,
          {
            opacity: 0,
            scale: 1.45,
            duration: 0.55
          },
          0.45
        );
      }

      tl.to(
        lines,
        {
          opacity: 1,
          x: 0,
          y: 0,
          scale: 1,
          rotate: 0,
          skewX: 0,
          ...(liteEffects ? {} : { filter: 'blur(0px)' }),
          stagger: liteEffects ? 0.06 : 0.14,
          duration: 0.7
        },
        liteEffects ? 0 : 0.12
      );
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section id="about" ref={sectionRef}>
      <div className="aboutContentWrapper" id="site-blackhole-anchor">
        <div className="aboutEmission" aria-hidden="true" />
        <div className="aboutText">
          <p className="aboutEyebrow aboutReveal">About</p>
          <h2 className="aboutReveal">
            I’m Kevin — I build <span className="projectTextHeadingFancy">web apps</span>
          </h2>
          <p className="aboutLede aboutReveal">
            Full-stack engineer in Bloomington, Indiana. For {yearsExperience}+ years I’ve
            designed and shipped products people actually use: recipe platforms, course
            sites, restaurant tools, and public data apps, mostly in React, TypeScript,
            and Node.
          </p>
          <div className="aboutLinks aboutReveal">
            <a href="#projects">See projects</a>
            <a href="#contact">Get in touch</a>
          </div>
        </div>
      </div>
      <div className="triangle" />
    </section>
  );
};

export default About;
